import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Search, Instagram, Trash2, Edit3, X, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { name: '', instagram: '', website: '', notes: '', is_active: true };

/**
 * Admin catalogue for Organizadores → Instagram del Organizador.
 * When creating events, the "Instagram del Organizador" input can autofill from
 * this catalogue when the operator types the organizer's name.
 */
export default function OrganizersTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [merging, setMerging] = useState(false);
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (showInactive) params.set('include_inactive', 'true');
      params.set('limit', '500');
      const res = await axios.get(`${API_URL}/api/admin/organizers?${params.toString()}`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar organizadores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleMergeDuplicates = async () => {
    if (!window.confirm(
      'Se fusionarán organizadores que apuntan al mismo Instagram. ' +
      'La entrada con más información (sitio web, usos, nombre canónico) se conserva ' +
      'y las demás se eliminan. ¿Continuar?'
    )) return;
    setMerging(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/organizers/merge-duplicates`);
      const { groups_found, merged, events_updated } = res.data || {};
      if (merged) {
        toast.success(
          `${merged} organizador${merged === 1 ? '' : 'es'} fusionado${merged === 1 ? '' : 's'}`,
          { description: `${groups_found} grupo(s) de duplicados. ${events_updated} evento(s) reasignado(s).` }
        );
      } else {
        toast.info('No se encontraron duplicados por handle de Instagram.');
      }
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al fusionar duplicados');
    } finally {
      setMerging(false);
    }
  };

  // Client-side filter: server already filtered by q but keep this reactive for typeahead
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((o) =>
      (o.name || '').toLowerCase().includes(term) ||
      (o.instagram || '').toLowerCase().includes(term)
    );
  }, [items, q]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (o) => {
    setEditingId(o.id);
    setForm({
      name: o.name || '',
      instagram: o.instagram || '',
      website: o.website || '',
      notes: o.notes || '',
      is_active: !!o.is_active,
    });
    setDialogOpen(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.put(`${API_URL}/api/admin/organizers/${editingId}`, form);
        setItems((prev) => prev.map((o) => (o.id === editingId ? res.data : o)));
        toast.success('Organizador actualizado');
      } else {
        const res = await axios.post(`${API_URL}/api/admin/organizers`, form);
        setItems((prev) => [res.data, ...prev]);
        toast.success('Organizador creado');
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/admin/organizers/${deleteTarget.id}`);
      setItems((prev) => prev.filter((o) => o.id !== deleteTarget.id));
      toast.success('Organizador eliminado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-pink-50 to-white border border-pink-100 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-pink-600 text-white flex items-center justify-center shrink-0">
              <Instagram className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#064E3B]">Organizadores</h2>
              <p className="text-sm text-gray-600 max-w-md">
                Registra el nombre del organizador y su Instagram. Al crear un evento, se puede autocompletar el IG desde este catálogo.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={openCreate} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="organizer-create-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuevo organizador
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-white p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o handle IG…"
            className="pl-9"
            data-testid="organizer-search-input"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <Switch checked={showInactive} onCheckedChange={(v) => { setShowInactive(v); setTimeout(fetchAll, 0); }} />
          Ver inactivos
        </label>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refrescar'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleMergeDuplicates}
          disabled={merging}
          data-testid="organizers-merge-duplicates"
          title="Fusiona organizadores que apuntan al mismo Instagram (ignora URL params y @-prefix)"
        >
          {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fusionar duplicados'}
        </Button>
      </div>

      {/* List */}
      <div className="rounded-xl border bg-white overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            {q ? 'Sin resultados. ' : 'Aún no hay organizadores. '}
            <button className="text-pink-700 underline" onClick={openCreate}>Crea el primero.</button>
          </div>
        ) : (
          <div className="divide-y">
            {filtered.map((o) => (
              <div key={o.id} className="p-3 flex items-center gap-3" data-testid={`organizer-row-${o.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#064E3B] truncate flex items-center gap-2">
                    {o.name}
                    {!o.is_active && <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">Inactivo</span>}
                  </div>
                  <div className="text-[12px] text-gray-500 truncate flex items-center gap-1.5">
                    <Instagram className="w-3 h-3 text-pink-500" />
                    {o.instagram || <span className="italic text-gray-400">Sin IG</span>}
                  </div>
                  {o.notes && <div className="text-[11px] text-gray-400 mt-0.5 truncate">{o.notes}</div>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid={`organizer-edit-${o.id}`}>
                  <Edit3 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteTarget(o)} data-testid={`organizer-delete-${o.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar organizador' : 'Nuevo organizador'}</DialogTitle>
            <DialogDescription>
              El nombre es único. El Instagram se autocompletará en el formulario de eventos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: Cirque du Soleil"
                data-testid="organizer-form-name"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Instagram className="w-3 h-3 text-pink-500" /> Instagram</Label>
              <Input
                value={form.instagram}
                onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                placeholder="@handle o https://instagram.com/handle"
                data-testid="organizer-form-instagram"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Globe className="w-3 h-3 text-emerald-600" /> Sitio web</Label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://www.passline.com"
                data-testid="organizer-form-website"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Cuando se hace scraping de un evento, si su URL de compra pertenece a este dominio (o el nombre del organizador aparece en la URL), el IG del organizador se autocompleta.
              </p>
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Referencias, contacto interno, etc."
                rows={2}
                data-testid="organizer-form-notes"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="organizer-form-active" />
              Activo (aparece en el autocomplete del formulario)
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              <X className="w-4 h-4 mr-1" /> Cancelar
            </Button>
            <Button onClick={saveForm} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="organizer-form-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar organizador</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Confirmas eliminar <b>{deleteTarget?.name}</b>? Los eventos que ya usaron su IG no se modificarán.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
